import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { auth, db } from "../contexts/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, updateDoc, collection, addDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import GeneratePageBanner from "../components/ads/GeneratePageBanner";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../contexts/firebase";
import { SparklesIcon } from "@heroicons/react/outline";
import Toast from "../components/Toast";

export default function Generate() {
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [credits, setCredits] = useState(0);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const incoming = location.state || {};
  const [activeTab, setActiveTab] = useState("image"); // Tabs


  const [prompt, setPrompt] = useState(incoming.prompt || "");
  const [seed, setSeed] = useState(
    typeof incoming.seed === "number" ? incoming.seed : -1
  );
  const [width, setWidth] = useState(incoming.width || 768);
  const [height, setHeight] = useState(incoming.height || 768);

  const isLocal = window.location.hostname.includes("localhost");
  const BASE_URL = isLocal ? "https://geneartai.com/api/replicate" : "/api/replicate";
  const ENHANCE_URL = isLocal
    ? "https://geneartai.com/api/enhance-prompt"
    : "/api/enhance-prompt";

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const backgroundImages = isMobile
    ? ["https://geneartai.com/images/mobile1.png"]
    : ["https://geneartai.com/images/image1.png"];
  const [enhancing, setEnhancing] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "" });
  const [enhanceElapsed, setEnhanceElapsed] = useState(0);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const docRef = doc(db, "users", currentUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setCredits(docSnap.data().credits || 0);
        } else {
          setCredits(0);
        }
      } else {
        setCredits(0);
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % backgroundImages.length);
    }, 7000);
    return () => clearInterval(interval);
  }, []);

  const generateImage = async () => {
    setError("");
    if (credits <= 0) {
      setError("You don't have enough credits!");
      setToast({ message: "You don't have enough credits!", type: "error" });
      return;
    }
    if (!prompt.trim()) {
      setError("Please enter a prompt.");
      return;
    }

    setLoading(true);
    setImage(null);
    setElapsedTime(0);

    const interval = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);

    try {
      const payload = {
        prompt,
        width: Number(width),
        height: Number(height),
      };

      // Only include seed if it's a non-negative integer
      if (Number.isInteger(seed) && seed > -2) {
        payload.seed = seed;
      }

      // Step 1: Start generation request
      const createRes = await fetch(BASE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const createData = await createRes.json();

      if (!createData.id) throw new Error("Invalid prediction response");

      const predictionId = createData.id;
      let finalImageUrl = null;
      let polling = true;

      // Step 2: Polling
      while (polling) {
        const pollRes = await fetch(`${BASE_URL}?id=${predictionId}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        const pollData = await pollRes.json();
        if (pollData.status === "succeeded") {
          finalImageUrl = Array.isArray(pollData.output)
            ? pollData.output[pollData.output.length - 1]
            : pollData.output;
          polling = false;
        } else if (pollData.status === "failed") {
          throw new Error("Prediction failed.");
        } else {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }

      if (finalImageUrl) {
        // Step 3: Download from Replicate
        const response = await fetch(finalImageUrl);
        const blob = await response.blob();
        // ensure it's really an image
        const contentType = blob.type.startsWith("image/")
          ? blob.type
          : "image/png";

        // Step 4: Upload to Firebase Storage (critical step)
        const filename = `users/${user.uid}/images/${Date.now()}.png`;
        const storageRef = ref(storage, filename);

        // upload with correct metadata
        await uploadBytes(storageRef, blob, {
          contentType: blob.type
        });

        try {
          await uploadBytes(storageRef, blob);
        } catch (uploadError) {
          console.error("Firebase upload failed:", uploadError);
          setError("Failed to upload generated image. Please try again.");
          return; // ❗ stop here, don't deduct credits or show success
        }

        // Step 5: Get Public URL
        const storageUrl = await getDownloadURL(storageRef);

        // Step 6: Save reference to Firestore
        await addDoc(collection(db, "users", user.uid, "images"), {
          prompt: prompt,
          seed: seed,
          width: width,
          height: height,
          imageUrl: storageUrl,
          timestamp: serverTimestamp(),
        });

        // Step 7: Update UI
        setImage(storageUrl);
        confetti({ particleCount: 40, spread: 60, origin: { y: 0.6 } });
        confetti({ particleCount: 40, spread: 80, origin: { y: 0.5 } });

        // Step 8: Update Credits (only if everything succeeded)
        const userDocRef = doc(db, "users", user.uid);
        await updateDoc(userDocRef, { credits: credits - 1 });
        setCredits((prev) => prev - 1);

      } else {
        setError("Failed to fetch final image. Try again.");
      }

    } catch (error) {
      console.error(error);
      setError("An error occurred. Please try again later.");
    } finally {
      clearInterval(interval);
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!image) return;
    try {
      const response = await fetch(image);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `geneartai_image_${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the image:", error);
    }
  };

  const enhancePrompt = async () => {
    if (!prompt.trim()) {
      setToast({ message: "Please enter a prompt to enhance.", type: "info" });
      return;
    }

    if (credits <= 0) {
      setToast({ message: "You don't have enough credits!", type: "error" });
      return;
    }

    setEnhancing(true);
    setError("");
    setEnhanceElapsed(0);

    const timer = setInterval(() => {
      setEnhanceElapsed((prev) => prev + 1);
    }, 1000);

    try {
      // 1️⃣ Start enhancement by POST
      const startRes = await fetch(ENHANCE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt }),
      });

      const startData = await startRes.json();
      if (!startRes.ok || startData.error) {
        throw new Error(startData.error || "Failed to start enhancement");
      }

      const predictionId = startData.id;

      // 2️⃣ Poll every 2s
      let enhancedText = null;
      for (let i = 0; i < 60; i++) { // Try polling for max 2 mins
        const pollRes = await fetch(`${ENHANCE_URL}?id=${predictionId}`);
        const pollData = await pollRes.json();

        if (pollData.enhanced) {
          enhancedText = pollData.enhanced.trim();
          break;
        }

        if (pollData.error) {
          throw new Error(pollData.error);
        }

        // wait 2 sec before next poll
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      if (!enhancedText) {
        throw new Error("Enhancement took too long. Try again later.");
      }

      // 3️⃣ Enhancement successful
      setPrompt(enhancedText);

      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, { credits: credits - 1 });
      setCredits((prev) => prev - 1);

      setToast({ message: "✨ Prompt enhanced!", type: "success" });

    } catch (err) {
      console.error("Error enhancing prompt:", err);
      setToast({ message: err.message || "Enhance Failed. Try again later..!", type: "error" });
    } finally {
      clearInterval(timer);
      setEnhancing(false);
      setEnhanceElapsed(0);
    }
  };

  return (
    <div className="relative min-h-screen font-sans text-white flex flex-col overflow-x-hidden">
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "" })}
          duration={4000}
        />
      )}

      {/* Background */}
      <div
        className="fixed inset-0 z-0 transition-opacity duration-1000 ease-in-out bg-cover bg-center"
        style={{
          backgroundImage: `url(${backgroundImages[currentImageIndex]})`,
          backgroundAttachment: isMobile ? "scroll" : "fixed",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0"></div>
      </div>

      {/* Foreground Content */}
      <div className="relative z-10 flex flex-col flex-grow">

        {/* Top Spacer for Navbar */}
        <div className="pt-16 sm:pt-16 md:pt-28"></div>

        {/* Ad Banner */}
        <div className="hidden md:block">
          <GeneratePageBanner />
        </div>

        {/* Main Section */}
        <div className="flex flex-col lg:flex-row gap-8 w-full max-w-7xl mx-auto p-4 md:p-2">

          <motion.div
            className="flex-1 bg-black/10 text-gray-800 rounded-2xl shadow-2xl p-8 flex flex-col md:flex-row gap-8 backdrop-blur-md"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >

            <div className="flex flex-col md:flex-row gap-8 w-full">
              {/* Left Controls */}
              <div className="w-full md:w-1/3 flex flex-col justify-start">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-6 text-center md:text-left">
                  Generate AI Image 🎨
                </h1>

                {/* Model */}
                <label className="text-base font-semibold text-white mb-1">Model</label>
                <select
                  className="mb-4 px-4 py-1.5 rounded-lg bg-black/30 border border-gray-600 text-white text-base"
                  defaultValue="stable-diffusion"
                >
                  <option value="stable-diffusion">Stable Diffusion</option>
                  <option value="openjourney">OpenJourney</option>
                </select>

                {/* Seed */}
                <label className="text-base font-semibold text-white mt-2">Seed (optional)</label>
                <input
                  type="number"
                  value={seed}
                  min={-1}
                  max={999999}
                  onChange={(e) => setSeed(e.target.value)}
                  className="mb-4 px-4 py-1.5 rounded-lg bg-black/30 border border-gray-600 text-white"
                />

                {/* Resolution */}
                <label className="text-base font-semibold text-white">Resolution</label>
                <select
                  value={`${width}x${height}`}
                  onChange={(e) => {
                    const [w, h] = e.target.value.split("x").map(Number);
                    setWidth(w);
                    setHeight(h);
                  }}
                  className="mb-4 px-4 py-1.5 rounded-lg bg-black/30 border border-gray-600 text-white"
                >
                  <option value="2048x2048">Square, 2048x2048</option>
                  <option value="1536x2048">Portrait, 1536x2048</option>
                  <option value="2048x1536">Landscape, 2048x1536</option>
                  <option value="2048x1152">Widescreen, 2048x1152</option>
                </select>

                {/* Error */}
                {error && (
                  <div className="bg-red-500/20 text-red-300 px-4 py-2 rounded-lg text-center mb-4 border border-red-400">
                    {error}
                  </div>
                )}

                {/* Desktop-only Generate Button */}
                <div className="hidden md:block">
                  <button
                    onClick={generateImage}
                    disabled={loading || enhancing}
                    className={`w-full py-1 rounded-full font-semibold text-lg ${loading
                      ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                      : "bg-blue-800 hover:bg-[#166CB0] text-white"
                      } transition duration-300 flex justify-center items-center gap-2`}
                  >
                    {loading ? "Generating..." : "Generate Image"}
                  </button>
                  {loading && (
                    <p className="text-center text-sm text-white mt-2">
                      Elapsed Time: {elapsedTime} seconds
                    </p>
                  )}
                </div>
              </div>

              {/* Right Content: Prompt & Image */}
              <div className="w-full md:w-2/3 flex flex-col justify-start gap-4">

                {/* Prompt + Enhance */}
                <div className="w-full flex flex-col gap-2">
                  <div className="flex justify-between items-center">
                    <label className="text-base font-semibold text-white">Prompt</label>
                    <button
                      type="button"
                      onClick={enhancePrompt}
                      disabled={enhancing || loading}
                      className={`text-xs sm:text-sm flex items-center gap-1 px-3 py-1.5 rounded-md font-semibold shadow-md transition ${enhancing || loading
                          ? "bg-yellow-300 text-yellow-800 opacity-70 cursor-not-allowed"
                          : "bg-yellow-400 text-yellow-900 hover:bg-yellow-300 animate-bounce"
                        }`}
                    >
                      <SparklesIcon className="h-4 w-4" />
                      {enhancing ? `Enhancing… (${enhanceElapsed}s)` : "Enhance Prompt"}
                    </button>
                  </div>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    disabled={enhancing || loading}
                    placeholder="e.g. A futuristic city skyline at night with neon lights and flying cars"
                    className="w-full px-4 py-2 rounded-lg bg-black/30 border border-gray-600 text-white resize-y min-h-[8rem]"
                  />
                </div>

                {/* Mobile Generate Button */}
                <div className="block md:hidden w-full">
                  <button
                    onClick={generateImage}
                    disabled={loading || enhancing}
                    className={`w-full py-1 rounded-full font-semibold text-lg ${loading
                        ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                        : "bg-blue-800 hover:bg-[#166CB0] text-white"
                      } transition duration-300 flex justify-center items-center gap-2`}
                  >
                    {loading ? "Generating..." : "Generate Image"}
                  </button>
                  {loading && (
                    <p className="text-center text-sm text-white mt-2">
                      Elapsed Time: {elapsedTime} seconds
                    </p>
                  )}
                </div>

                {/* Image Display */}
                {image ? (
                  <motion.div
                    className="relative group w-full flex justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                  >
                    <img
                      src={image}
                      alt="Generated Art"
                      className="rounded-lg shadow-md max-w-full max-h-[500px] object-contain"
                    />
                    <button
                      type="button"
                      onClick={handleDownload}
                      className="absolute top-4 right-4 bg-white text-[#166CB0] rounded-full p-2 shadow-lg opacity-0 group-hover:opacity-100 transition duration-300 transform group-hover:scale-110"
                      title="Download Image"
                    >
                      ⬇️
                    </button>
                  </motion.div>
                ) : (
                  <div className="w-full h-[400px] flex items-center justify-center rounded-lg bg-black/10 border-2 border-dashed border-gray-400">
                    <p className="text-gray-400 text-center text-lg">Your generated image will appear here</p>
                  </div>
                )}
              </div>

            </div>

          </motion.div>

        </div>

        {/* Footer */}
        <footer className="text-center text-xs text-gray-300 pb-3 mt-auto z-10">
          <div>© 2025 GeneArtAI — All Rights Reserved.</div>
          <div className="mt-2">
            <Link to="/privacy-policy" className="text-gray-300 hover:text-white underline">
              Privacy Policy
            </Link>{" "}
            |{" "}
            <Link to="/terms-of-service" className="underline hover:text-white">
              Terms of Service
            </Link>
          </div>
        </footer>
        {/* Spacer below footer only on mobile for MobileBottomAd */}
        <div className="block sm:hidden h-20" />

      </div >
    </div >
  );
}
